import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { Product } from "../types/product";
import {
  catalogCacheKey,
  getCatalogUserKey,
  readCatalogCache,
  writeCatalogCache,
} from "../lib/catalogCache";
import { loadProductsCatalog } from "../services/catalogService";

interface ProductsContextProps {
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  updateProductQuantity: (id: string, newQuantity: number) => void;
  deleteProduct: (id: string) => void;
  catalogLoading: boolean;
  refreshCatalog: (options?: { force?: boolean }) => Promise<void>;
}

const ProductsContext = createContext<ProductsContextProps | null>(null);

const isPosPath = () =>
  window.location.pathname.includes("/pos") ||
  window.location.pathname === "/pos";

const normalizeCatalogProducts = (items: Product[]): Product[] =>
  items.map((p) => {
    let status: Product["status"] = "In Stock";
    if ((p.stock ?? 0) === 0) status = "Out of Stock";
    else if ((p.stock ?? 0) < 10) status = "Low Stock";
    const { quantity, ...productWithoutQuantity } = p;
    return { ...productWithoutQuantity, status };
  });

export const ProductsProvider = ({ children }: { children: ReactNode }) => {
  const [products, setProducts] = useState<Product[]>(() => {
    if (isPosPath()) return [];

    const userKey = getCatalogUserKey();
    if (userKey) {
      const cached = readCatalogCache<Product[]>(
        catalogCacheKey("products", userKey),
      );
      if (cached?.data?.length) {
        return normalizeCatalogProducts(cached.data);
      }
    }

    return [];
  });
  const [catalogLoading, setCatalogLoading] = useState(false);

  const refreshCatalog = useCallback(async (options?: { force?: boolean }) => {
    const token = localStorage.getItem("token");
    const userKey = getCatalogUserKey();
    if (!token || !userKey || isPosPath()) return;

    setCatalogLoading(true);
    try {
      const fresh = await loadProductsCatalog(userKey, {
        force: options?.force,
        onCached: (cached) => {
          if (cached.length > 0) {
            setProducts(normalizeCatalogProducts(cached));
          }
        },
      });
      if (fresh.length > 0) {
        setProducts(normalizeCatalogProducts(fresh));
      }
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      refreshCatalog();
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [refreshCatalog]);

  useEffect(() => {
    const isCartData =
      products.length > 0 &&
      products.some((p) => p.quantity !== undefined && p.quantity > 0);

    if (isCartData || products.length === 0 || isPosPath()) return;

    const userKey = getCatalogUserKey();
    if (userKey) {
      writeCatalogCache(catalogCacheKey("products", userKey), products);
    }
  }, [products]);

  const updateProductQuantity = (id: string, newQuantity: number) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, quantity: newQuantity < 0 ? 0 : newQuantity } : p,
      ),
    );
  };

  const deleteProduct = (id: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <ProductsContext.Provider
      value={{
        products,
        setProducts,
        updateProductQuantity,
        deleteProduct,
        catalogLoading,
        refreshCatalog,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) {
    throw new Error("useProducts must be used within a ProductsProvider");
  }
  return ctx;
};
